interface FAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
}

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

export interface ChatResponse {
  content: string;
  source: 'faq' | 'context' | 'default' | 'api';
  faqId?: string;
}

// Simple tokenization and normalization
function normalizeText(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[.,!?;:'"]/g, '')
    .split(/\s+/)
    .filter(word => word.length > 2);
}

// Calculate similarity score between user message and FAQ question
function calculateSimilarity(userTokens: string[], faqTokens: string[]): number {
  if (userTokens.length === 0 || faqTokens.length === 0) return 0;

  const userSet = new Set(userTokens);
  const faqSet = new Set(faqTokens);

  const intersection = new Set([...userSet].filter(x => faqSet.has(x)));

  // Use Jaccard similarity
  const union = new Set([...userSet, ...faqSet]);
  return intersection.size / union.size;
}

// Find best matching FAQ using semantic similarity
export function findBestFAQ(userMessage: string, faqs: FAQ[]): { faq: FAQ; score: number } | null {
  const userTokens = normalizeText(userMessage);

  if (userTokens.length === 0 || faqs.length === 0) return null;

  let bestMatch = null;
  let bestScore = 0.15; // Very low threshold

  for (const faq of faqs) {
    const faqTokens = normalizeText(faq.question);
    const score = calculateSimilarity(userTokens, faqTokens);

    if (score > bestScore) {
      bestScore = score;
      bestMatch = faq;
    }
  }

  return bestMatch ? { faq: bestMatch, score: bestScore } : null;
}

// Keywords that trigger specific behaviors
const keywordPatterns = {
  pricing: /prix|coût|cout|tarif|combien|payer|gratuit|free|plan|abonnement/i,
  contact: /contact|consultation|appel|réunion|devis|commercial/i,
  roi: /roi|retour|résultat|efficacité|performance|augment|croiss|améli|gain|profit/i,
  implementation: /implement|intégr|setup|install|déployer|lancer|commencer|démarre/i,
  features: /fonction|capacité|fonctionnalité|peut|possible|inclus|feature|outil/i,
  urgent: /urgent|immédiat|vite|rapide|asap|maintes|besoin vite/i,
  technical: /erreur|bug|problème|ne fonctionne|crash|erreur|error|fail/i,
  greeting: /bonjour|salut|hello|hi|ça va|comment|bonsoir|coucou/i,
};

// Detect user intent from message
export function detectIntent(message: string): string {
  for (const [intent, pattern] of Object.entries(keywordPatterns)) {
    if (pattern.test(message)) {
      return intent;
    }
  }
  return 'general';
}

// Generate contextual response based on intent and message
export function generateContextualResponse(
  userMessage: string,
  intent: string,
  bestFAQ: { faq: FAQ; score: number } | null
): ChatResponse {
  // If similarity match found, return FAQ answer
  if (bestFAQ && bestFAQ.score > 0.2) {
    return {
      content: bestFAQ.faq.answer,
      source: 'faq',
      faqId: bestFAQ.faq.id,
    };
  }

  // Context-aware responses for specific intents
  const contextResponses: { [key: string]: string } = {
    pricing: 'Nos tarifs sont adaptés à la taille et aux besoins de votre entreprise. Je vous recommande de planifier une consultation avec notre équipe pour obtenir une offre personnalisée. Souhaitez-vous être contacté?',
    roi: 'Notre plateforme IA marketing a aidé nos clients à augmenter leur ROI en moyenne de 40%. Voulez-vous connaître les cas de succès spécifiques ou planifier une démo?',
    implementation: 'L\'implémentation est généralement complétée en 2-4 semaines. Nous vous accompangnons à chaque étape. Voulez-vous en savoir plus sur le processus?',
    features: 'Nos services incluent l\'automatisation marketing, l\'analyse prédictive, l\'optimisation des campagnes, et bien plus. Quelle fonctionnalité vous intéresse le plus?',
    contact: 'Excellent! Pour parler avec notre équipe, vous pouvez nous contacter au +33 189 480 425 ou remplir notre formulaire de contact. Nous vous recontacterons rapidement!',
    urgent: 'Je comprends l\'urgence. Contactez notre équipe directement au +33 189 480 425 pour un suivi immédiat.',
    technical: 'Si vous rencontrez un problème technique, veuillez nous contacter via le formulaire ou au +33 189 480 425. Notre équipe technique vous aidera rapidement.',
    greeting: 'Bonjour! Je suis l\'assistant Nova Communication. Je peux vous aider avec nos services IA marketing, nos solutions, nos tarifs, et plus. Comment puis-je vous aider?',
    general: 'Merci pour votre question! Nous aidons les entreprises à transformer leur marketing avec l\'IA. Posez-moi des questions sur nos services, notre processus, nos tarifs, ou planifiez une consultation!',
  };

  return {
    content: contextResponses[intent] || contextResponses['general'],
    source: 'context',
  };
}

// Quick reply suggestions based on conversation context
export function getQuickReplies(
  conversationLength: number,
  lastBotResponse: string
): string[] {
  // Start of conversation
  if (conversationLength <= 2) {
    return [
      'Nos services',
      'Nos tarifs',
      'Demander une démo',
    ];
  }

  // FAQ-related quick replies
  if (lastBotResponse.includes('ROI') || lastBotResponse.includes('résultats')) {
    return [
      'Cas d\'usage',
      'Exemples de ROI',
      'Consulter un expert',
    ];
  }

  // Default quick replies
  return [
    'Plus d\'infos',
    'Me contacter',
    'Retour au début',
  ];
}

// Format message for storage
export function formatMessageForStorage(message: Message): Record<string, any> {
  return {
    id: message.id,
    type: message.type,
    content: message.content,
    timestamp: message.timestamp.toISOString(),
  };
}

// Validation for empty or spam messages
export function validateUserMessage(message: string): { valid: boolean; reason?: string } {
  const trimmed = message.trim();

  if (!trimmed) {
    return { valid: false, reason: 'empty' };
  }

  if (trimmed.length > 500) {
    return { valid: false, reason: 'too_long' };
  }

  // Simple spam detection
  if (/^(.+?)\1{4,}/.test(trimmed)) {
    return { valid: false, reason: 'spam' };
  }

  return { valid: true };
}